import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { Observable, take } from "rxjs";
import { HttpService } from "../../shared/services/http.service";
import { AuthService } from "../../shared/services/auth.service";
import { AppConfig } from "../../app.config";

export interface EC2InstanceItem {
  id: string;
  region: string;
  name: string;
  description: string | null;
  state: string | null;
  /** Present when user is compute admin (backend includes per-instance user list) */
  users?: InstanceUser[];
}

export interface CreateEC2InstanceRequest {
  id: string;
  region?: string;
  name?: string;
  description?: string;
}

/** Simple shape for a user used in per-instance access lists. */
export interface InstanceUser {
  id: string;
  email: string;
}

@Injectable({
  providedIn: 'root'
})
export class Ec2InstancesService {
  private readonly baseUrl = `${AppConfig.settings.apiUrl_v2}/ec2-instances`;

  constructor(
    private http: HttpService,
    private authService: AuthService
  ) {}

  private getHeaders(): { headers: HttpHeaders } {
    const headers = new HttpHeaders().append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );
    return { headers };
  }

  list(): Observable<EC2InstanceItem[]> {
    return this.http.get(this.baseUrl + "/", this.getHeaders());
  }

  create(body: CreateEC2InstanceRequest): Observable<{ message: string }> {
    return this.http.post(this.baseUrl + "/", body, this.getHeaders());
  }

  changeState(
    instanceId: string,
    action: "start" | "stop" | "reboot"
  ): Observable<{ state: string }> {
    return this.http.patch<{ state: string }>(
      `${this.baseUrl}/${instanceId}/`,
      { action },
      this.getHeaders()
    );
  }

  /** Delete an EC2 instance (compute admins only). */
  delete(instanceId: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/${instanceId}/`, this.getHeaders());
  }

  /**
   * Fetch users that currently have access to an EC2 instance.
   * Assumes backend exposes: GET /ec2-instances/{id}/users/
   */
  listInstanceUsers(instanceId: string): Observable<InstanceUser[]> {
    return this.http.get(
      `${this.baseUrl}/${instanceId}/users/`,
      this.getHeaders()
    );
  }

  /**
   * Update the list of users that have access to an EC2 instance.
   * Assumes backend accepts: PUT /ec2-instances/{id}/users/ with { user_ids: [...] }.
   */
  updateInstanceUsers(
    instanceId: string,
    userIds: string[]
  ): Observable<unknown> {
    return this.http.put(
      `${this.baseUrl}/${instanceId}/users/`,
      { user_ids: userIds },
      this.getHeaders()
    );
  }
}
